import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { Send } from 'lucide-react';

// Markdown component overrides for readable assistant messages (lists, paragraphs)
const markdownComponents = {
  p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-4 my-2 space-y-1" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-4 my-2 space-y-1" {...props} />,
  li: ({ node, ...props }) => <li className="mb-1" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
};

/**
 * Reusable chat UI: messages list + input.
 * Used by EmbeddedChatbot (floating) and StepperChatbotWidget (inline).
 */
const ChatContent = ({
  messages,
  inputMessage,
  onInputChange,
  onSendMessage,
  onKeyPress,
  isLoading,
  conversationEnded,
  hasDocument,
  primaryColor,
  primaryColorHover,
  maxWidthClass = 'sm:max-w-[280px]',
  onOfferLinkClick,
  className = '',
}) => {
  const { t } = useTranslation();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const wasLoadingRef = useRef(false);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  useEffect(() => {
    const wasLoading = wasLoadingRef.current;
    wasLoadingRef.current = isLoading;
    if (!wasLoading || isLoading || conversationEnded || !hasDocument) return;
    inputRef.current?.focus({ preventScroll: true });
  }, [isLoading, conversationEnded, hasDocument]);

  return (
    <div className={`flex flex-col flex-1 min-h-0 ${className}`}>
      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-3 sm:px-4 py-4 space-y-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`px-4 py-3 rounded-lg shadow-sm max-w-[85vw] ${maxWidthClass} ${
                message.role === 'user' ? 'text-white' : 'bg-gray-100 text-gray-800 border border-gray-200'
              }`}
              style={message.role === 'user' ? { backgroundColor: primaryColor } : undefined}
            >
              <div className={`text-sm leading-relaxed ${message.role === 'user' ? 'whitespace-pre-wrap' : ''}`}>
                {message.offerLink ? (
                  <div>
                    <p className="mb-3">{message.content}</p>
                    {onOfferLinkClick ? (
                      <button
                        type="button"
                        onClick={() => onOfferLinkClick(message.offerLink)}
                        className="inline-block px-4 py-2 text-white rounded-md transition-colors font-medium"
                        style={{ backgroundColor: primaryColor }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = primaryColorHover; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = primaryColor; }}
                      >
                        {t('embed.continueToOffer')}
                      </button>
                    ) : (
                      <a
                        href={message.offerLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block px-4 py-2 text-white rounded-md transition-colors font-medium"
                        style={{ backgroundColor: primaryColor }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = primaryColorHover; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = primaryColor; }}
                      >
                        {t('embed.viewYourOffer')}
                      </a>
                    )}
                  </div>
                ) : message.role === 'assistant' ? (
                  <ReactMarkdown components={markdownComponents}>{message.content}</ReactMarkdown>
                ) : (
                  message.content
                )}
              </div>
              <div
                className={`text-xs mt-2 opacity-70 ${message.role === 'user' ? '' : 'text-gray-500'}`}
                style={message.role === 'user' ? { color: 'rgba(255,255,255,0.85)' } : undefined}
              >
                {message.role === 'user' ? t('embed.you') : t('embed.aiAssistant')}
              </div>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 text-gray-800 px-4 py-3 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
                <span className="text-xs text-gray-500">{t('embed.aiTyping')}</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-gray-200">
        {conversationEnded ? (
          <div className="text-center py-2">
            <p className="text-sm text-gray-500 italic">{t('embed.conversationEnded')}</p>
          </div>
        ) : (
          <div className="flex space-x-3">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => onInputChange(e.target.value)}
              onKeyPress={onKeyPress}
              placeholder={hasDocument ? t('embed.typeMessage') : t('embed.chatbotUnavailable')}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0 text-sm min-h-[44px]"
              style={{ ['--tw-ring-color']: primaryColor }}
              disabled={isLoading || conversationEnded || !hasDocument}
            />
            <button
              onClick={onSendMessage}
              disabled={!inputMessage.trim() || isLoading || conversationEnded || !hasDocument}
              className="text-white p-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              style={{ backgroundColor: primaryColor }}
              onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = primaryColorHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = primaryColor; }}
            >
              <Send size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatContent;
